/*
 * Menu de categorias da LP do Bolão: clicar numa categoria revela os atletas dela.
 *
 * Só leitura — quem vota é <ec-olympia-bolao>. Por isso consome /atletas e não
 * /ranking: aqui a ordem é a do banco (atleta da casa primeiro, depois nome), e
 * não a apuração, que mudaria a lista a cada voto de terceiro.
 *
 * Nome de atleta é dado de terceiro e entra só por textContent.
 */
{
  class EcBolaoCategorias extends HTMLElement {
    connectedCallback() {
      if (this.iniciado) return;
      this.iniciado = true;

      const d = this.dataset;
      this.endpoint = (d.endpoint || '').replace(/\/$/, '');

      this.menu = this.querySelector('[data-menu]');
      this.painel = this.querySelector('[data-painel]');
      this.lista = this.querySelector('[data-lista]');
      this.rotulo = this.querySelector('[data-rotulo]');
      // Por gancho, não por tag: o menu também é um <olympia-rolagem>, e vem antes.
      this.rolagem = this.querySelector('[data-rolagem-atletas]');
      this.brasao = this.querySelector('template[data-brasao]');

      this.atual = null;
      // slug -> promise dos atletas; a categoria já aberta não volta ao Worker.
      this.cache = new Map();
      this.pedido = 0;

      if (!this.endpoint) {
        this.avisar('Endpoint do bolão não configurado nas definições do tema.');
        return;
      }

      this.iniciar();
    }

    async iniciar() {
      try {
        const r = await this.buscar('/categorias');
        const categorias = r.categorias || [];

        if (!categorias.length) {
          this.carregando(false);
          this.avisar('Nenhuma categoria aberta.');
          return;
        }

        this.menu.replaceChildren(...categorias.map((c) => this.montarBotao(c)));
        this.ligarTeclado(categorias);
        this.selecionar(categorias[0]);
      } catch (e) {
        this.carregando(false);
        this.avisar(`Não foi possível carregar as categorias. ${e.message || ''}`.trim());
      }
    }

    /* Setas andam pelo menu, como pede o padrão ARIA de tablist. */
    ligarTeclado(categorias) {
      this.menu.addEventListener('keydown', (ev) => {
        const passo = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[ev.key];
        if (!passo) return;
        ev.preventDefault();

        const atual = categorias.findIndex((c) => c.slug === this.atual);
        const proxima = categorias[(atual + passo + categorias.length) % categorias.length];
        this.menu.querySelector(`[data-slug="${CSS.escape(proxima.slug)}"]`)?.focus();
        this.selecionar(proxima);
      });
    }

    montarBotao(categoria) {
      const item = document.createElement('li');
      item.className = 'bolao-cat__menu-item';
      // Sem isto o <li> fica entre o tablist e o tab, e a relação se perde.
      item.setAttribute('role', 'presentation');

      const botao = document.createElement('button');
      botao.type = 'button';
      botao.className = 'olympia-lp__aba bolao-cat__botao';
      botao.setAttribute('role', 'tab');
      botao.setAttribute('aria-selected', 'false');
      botao.id = `${this.painel.id}-aba-${categoria.slug}`;
      botao.setAttribute('aria-controls', this.painel.id);
      botao.tabIndex = -1;
      botao.dataset.slug = categoria.slug;
      botao.textContent = categoria.nome;
      botao.addEventListener('click', () => this.selecionar(categoria));

      item.append(botao);
      return item;
    }

    async selecionar(categoria) {
      if (this.atual === categoria.slug) return;
      this.atual = categoria.slug;

      for (const botao of this.menu.querySelectorAll('[data-slug]')) {
        const ativa = botao.dataset.slug === categoria.slug;
        botao.setAttribute('aria-selected', String(ativa));
        botao.tabIndex = ativa ? 0 : -1;
        if (ativa) this.painel.setAttribute('aria-labelledby', botao.id);
      }
      if (this.rotulo) this.rotulo.textContent = categoria.nome;

      // Só a resposta mais recente pinta a lista.
      const pedido = ++this.pedido;
      this.carregando(true);

      try {
        const r = await this.atletas(categoria.slug);
        if (pedido !== this.pedido) return;

        const atletas = r.atletas || [];
        if (!atletas.length) this.avisar('Nenhum atleta confirmado nesta categoria.');
        else this.lista.replaceChildren(...atletas.map((a) => this.montarLinha(a)));
      } catch {
        if (pedido === this.pedido) this.avisar('Não foi possível carregar os atletas.');
      } finally {
        if (pedido === this.pedido) this.carregando(false);
      }
    }

    atletas(slug) {
      if (!this.cache.has(slug)) {
        const promessa = this.buscar(`/atletas?categoria=${encodeURIComponent(slug)}`);
        // Falha não fica em cache: voltar à categoria tenta de novo.
        promessa.catch(() => this.cache.delete(slug));
        this.cache.set(slug, promessa);
      }
      return this.cache.get(slug);
    }

    montarLinha(atleta) {
      const item = document.createElement('li');
      item.className = 'bolao-cat__item';
      if (atleta.soldiers) item.dataset.soldiers = '';

      item.append(this.montarBandeira(atleta.pais));

      const nome = document.createElement('span');
      nome.className = 'bolao-cat__nome';
      nome.append(document.createTextNode(atleta.nome));
      if (atleta.soldiers && this.brasao) nome.append(this.brasao.content.cloneNode(true));
      item.append(nome);

      return item;
    }

    // Bandeira em imagem (o Windows não desenha emoji de bandeira); emoji só se a imagem falhar.
    montarBandeira(iso) {
      const caixa = document.createElement('span');
      caixa.className = 'bolao-cat__bandeira';
      if (!/^[a-z]{2}$/i.test(iso || '')) return caixa;

      const codigo = iso.toLowerCase();
      const img = document.createElement('img');
      img.src = `https://flagcdn.com/w40/${codigo}.png`;
      img.srcset = `https://flagcdn.com/w80/${codigo}.png 2x`;
      img.width = 22;
      img.height = 16;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.alt = '';
      img.addEventListener('error', () => caixa.replaceChildren(bandeira(iso)), { once: true });
      caixa.append(img);
      return caixa;
    }

    async buscar(caminho) {
      const resp = await fetch(`${this.endpoint}/api/bolao${caminho}`);
      const dados = await resp.json().catch(() => ({}));
      if (!resp.ok) throw Object.assign(new Error(dados.mensagem || 'Falha na consulta.'), { codigo: dados.erro });
      return dados;
    }

    avisar(texto) {
      const li = document.createElement('li');
      li.className = 'bolao-cat__vazio';
      li.textContent = texto;
      this.lista.replaceChildren(li);
    }

    carregando(ativo) {
      this.rolagem?.toggleAttribute('data-carregando', ativo);
      if (ativo) this.lista.setAttribute('aria-busy', 'true');
      else this.lista.removeAttribute('aria-busy');
    }
  }

  /* 'BR' -> 🇧🇷 */
  function bandeira(iso) {
    if (!iso || iso.length !== 2) return '';
    return String.fromCodePoint(...[...iso.toUpperCase()].map((c) => 0x1f1a5 + c.charCodeAt(0)));
  }

  if (!customElements.get('ec-bolao-categorias')) {
    customElements.define('ec-bolao-categorias', EcBolaoCategorias);
  }
}
